import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Users, 
  FileText, 
  Vote,
  Video,
  Mic,
  MicOff,
  VideoOff,
  Settings,
  Plus,
  Edit,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
  Play,
  Pause,
  Square,
  MapPin,
  Link,
  Download,
  Upload,
  Eye,
  User,
  Building2,
  Hash
} from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master.js';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/notifications/ToastProvider';
import LiveVotingModal from './LiveVotingModal';

const MeetingManagementModal = ({ meeting, isOpen, onClose, onMeetingUpdated }) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('agenda');
  const [agendaItems, setAgendaItems] = useState([]);
  const [motions, setMotions] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMotion, setSelectedMotion] = useState(null);
  const [showVotingModal, setShowVotingModal] = useState(false);
  const [meetingStatus, setMeetingStatus] = useState('scheduled');
  const [quorumStatus, setQuorumStatus] = useState(null);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  useEffect(() => {
    if (isOpen && meeting) {
      loadMeetingData();
      // Set up real-time subscription for meeting updates
      const subscription = masterSupabase
        .channel(`meeting_${meeting.id}`)
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'hoa_agenda_items',
            filter: `meeting_id=eq.${meeting.id}`
          }, 
          () => loadMeetingData()
        )
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'hoa_motions',
            filter: `meeting_id=eq.${meeting.id}`
          }, 
          () => loadMeetingData()
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [isOpen, meeting]);

  const loadMeetingData = async () => {
    if (!meeting) return;
    
    setLoading(true);
    try {
      // Load agenda items
      const { data: agendaData, error: agendaError } = await masterSupabase
        .from('hoa_agenda_items')
        .select('*')
        .eq('meeting_id', meeting.id)
        .order('order_index');

      if (agendaError) {
        console.error('Error loading agenda:', agendaError);
      } else {
        setAgendaItems(agendaData || []);
      }

      // Load motions
      const { data: motionsData, error: motionsError } = await masterSupabase
        .from('hoa_motions')
        .select(`
          *,
          hoa_votes(
            id,
            vote,
            weight_ppm,
            hoa_memberships(hoa_members(full_name))
          )
        `)
        .eq('meeting_id', meeting.id)
        .order('created_at');

      if (motionsError) {
        console.error('Error loading motions:', motionsError);
      } else {
        setMotions(motionsData || []);
      }

      // Load attendees (community members)
      const { data: attendeesData, error: attendeesError } = await masterSupabase
        .from('hoa_memberships')
        .select(`
          *,
          hoa_members(id, full_name, email),
          hoa_units(label)
        `)
        .eq('community_id', meeting.community_id)
        .eq('is_active', true);

      if (attendeesError) {
        console.error('Error loading attendees:', attendeesError);
      } else {
        setAttendees(attendeesData || []);
      }

      // Calculate quorum
      calculateQuorum(attendeesData || []);

    } catch (error) {
      console.error('Error loading meeting data:', error);
      addToast('Fehler beim Laden der Versammlungsdaten', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateQuorum = (attendeesData) => {
    const totalMembers = attendeesData.length;
    const requiredQuorum = Math.ceil(totalMembers * 0.5); // 50% Quorum
    const presentMembers = attendeesData.filter(a => a.is_present).length;
    
    setQuorumStatus({
      total: totalMembers,
      required: requiredQuorum,
      present: presentMembers,
      met: presentMembers >= requiredQuorum
    });
  };

  const startMeeting = async () => {
    try {
      const { error } = await masterSupabase
        .from('hoa_meetings')
        .update({
          status: 'in_progress',
          started_at: new Date().toISOString()
        })
        .eq('id', meeting.id);

      if (error) {
        console.error('Error starting meeting:', error);
        addToast('Fehler beim Starten der Versammlung', 'error');
        return;
      }

      setMeetingStatus('in_progress');
      addToast('Versammlung gestartet', 'success');
      
      if (onMeetingUpdated) {
        onMeetingUpdated(meeting.id);
      }
    } catch (error) {
      console.error('Error starting meeting:', error);
      addToast('Fehler beim Starten der Versammlung', 'error');
    }
  };

  const endMeeting = async () => {
    try {
      const { error } = await masterSupabase
        .from('hoa_meetings')
        .update({
          status: 'completed',
          ended_at: new Date().toISOString(),
          quorum_met: quorumStatus?.met || false
        })
        .eq('id', meeting.id);

      if (error) {
        console.error('Error ending meeting:', error);
        addToast('Fehler beim Beenden der Versammlung', 'error');
        return;
      }

      setMeetingStatus('completed');
      addToast('Versammlung beendet', 'success');
      
      if (onMeetingUpdated) {
        onMeetingUpdated(meeting.id);
      }
    } catch (error) {
      console.error('Error ending meeting:', error);
      addToast('Fehler beim Beenden der Versammlung', 'error');
    }
  };

  const openVotingModal = (motion) => {
    setSelectedMotion(motion);
    setShowVotingModal(true);
  };

  const handleVoteSubmitted = (motionId, voteType) => {
    // Reload meeting data to update vote counts
    loadMeetingData();
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
    addToast(isRecording ? 'Aufzeichnung gestoppt' : 'Aufzeichnung gestartet', 'info');
  };

  const joinVideoCall = () => {
    // This would integrate with Jitsi Meet or similar
    addToast('Video-Integration wird geladen...', 'info');
    // Implementation for video call integration
  };

  const getMotionStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'passed': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const calculateVotingProgress = (motion) => {
    if (!motion.hoa_votes || motion.hoa_votes.length === 0) {
      return { percentage: 0, yesVotes: 0, totalVotes: 0 };
    }
    
    const totalVotes = motion.hoa_votes.length;
    const yesVotes = motion.hoa_votes.filter(v => v.vote === 'yes').length;
    
    return { 
      percentage: Math.round((yesVotes / totalVotes) * 100),
      yesVotes,
      totalVotes
    };
  };

  const tabs = [
    { id: 'agenda', label: 'Tagesordnung', icon: FileText, count: agendaItems.length },
    { id: 'motions', label: 'Beschlüsse', icon: Vote, count: motions.length },
    { id: 'attendees', label: 'Teilnehmer', icon: Users, count: attendees.length },
    { id: 'video', label: 'Video-Konferenz', icon: Video, count: 0 }
  ];

  if (!isOpen || !meeting) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {meeting.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(meeting.scheduled_at).toLocaleDateString('de-DE')} um{' '}
                  {new Date(meeting.scheduled_at).toLocaleTimeString('de-DE', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Meeting Controls */}
              {meetingStatus === 'scheduled' && (
                <button
                  onClick={startMeeting}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                >
                  <Play className="w-4 h-4" />
                  <span>Versammlung starten</span>
                </button>
              )}
              
              {meetingStatus === 'in_progress' && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={toggleRecording}
                    className={`px-3 py-2 rounded-lg font-medium transition-colors flex items-center space-x-1 ${
                      isRecording 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-gray-600 hover:bg-gray-700 text-white'
                    }`}
                  >
                    {isRecording ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    <span>{isRecording ? 'Stop' : 'Aufzeichnen'}</span>
                  </button>
                  
                  <button
                    onClick={endMeeting}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <Square className="w-4 h-4" />
                    <span>Beenden</span>
                  </button>
                </div>
              )}
              
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Meeting Status & Quorum */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
                    meetingStatus === 'scheduled' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                    meetingStatus === 'in_progress' ? 'bg-green-100 text-green-800 border-green-200' :
                    'bg-gray-100 text-gray-800 border-gray-200'
                  }`}>
                    {meetingStatus === 'scheduled' ? 'Geplant' :
                     meetingStatus === 'in_progress' ? 'Läuft' : 'Beendet'}
                  </span>
                  {isRecording && (
                    <span className="flex items-center space-x-1 text-red-600 text-sm">
                      <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></div>
                      <span>Aufzeichnung</span>
                    </span>
                  )}
                </div>
                
                {quorumStatus && (
                  <div className="flex items-center space-x-2">
                    <span className={`flex items-center space-x-1 text-sm ${
                      quorumStatus.met ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {quorumStatus.met ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                      <span>
                        Quorum: {quorumStatus.present}/{quorumStatus.total} 
                        ({quorumStatus.met ? 'Beschlussfähig' : 'Nicht beschlussfähig'})
                      </span>
                    </span>
                  </div>
                )}
              </div>
              
              {meeting.is_online && (
                <button
                  onClick={joinVideoCall}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                >
                  <Video className="w-4 h-4" />
                  <span>Video beitreten</span>
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                    {tab.count > 0 && (
                      <span className={`
                        px-2 py-0.5 rounded-full text-xs font-medium border
                        ${activeTab === tab.id
                          ? 'bg-blue-100 text-blue-600 border-blue-200'
                          : 'bg-gray-100 text-gray-600 border-gray-200'
                        }
                      `}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Lade Versammlungsdaten...</p>
              </div>
            ) : (
              <>
                {/* Agenda Tab */}
                {activeTab === 'agenda' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Tagesordnung
                      </h3>
                      <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2">
                        <Plus className="w-4 h-4" />
                        <span>Punkt hinzufügen</span>
                      </button>
                    </div>
                    
                    {agendaItems.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          Keine Tagesordnungspunkte
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          Fügen Sie Punkte zur Tagesordnung hinzu
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {agendaItems.map((item, index) => (
                          <div key={item.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-3">
                                <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full text-sm font-medium">
                                  {index + 1}
                                </span>
                                <div>
                                  <h4 className="font-medium text-gray-900 dark:text-white">
                                    {item.title}
                                  </h4>
                                  {item.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                      {item.description}
                                    </p>
                                  )}
                                  {item.duration_minutes && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center space-x-1">
                                      <Clock className="w-3 h-3" />
                                      <span>{item.duration_minutes} Minuten</span>
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button className="text-gray-400 hover:text-red-600">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Motions Tab */}
                {activeTab === 'motions' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Beschlüsse & Abstimmungen
                      </h3>
                      <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2">
                        <Plus className="w-4 h-4" />
                        <span>Beschluss erstellen</span>
                      </button>
                    </div>
                    
                    {motions.length === 0 ? (
                      <div className="text-center py-8">
                        <Vote className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          Keine Beschlüsse
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          Erstellen Sie Beschlüsse für die Abstimmung
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {motions.map((motion) => {
                          const votingData = calculateVotingProgress(motion);
                          return (
                            <div key={motion.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                                    {motion.title}
                                  </h4>
                                  {motion.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                                      {motion.description}
                                    </p>
                                  )}
                                  <div className="flex items-center space-x-4 text-sm">
                                    <span className={`px-2 py-1 rounded-full font-medium border ${getMotionStatusColor(motion.status)}`}>
                                      {motion.status === 'open' ? 'Abstimmung läuft' :
                                       motion.status === 'passed' ? 'Angenommen' : 'Abgelehnt'}
                                    </span>
                                    <span className="text-gray-500 dark:text-gray-400">
                                      {votingData.totalVotes} Stimmen
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => openVotingModal(motion)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1"
                                >
                                  <Vote className="w-4 h-4" />
                                  <span>Abstimmen</span>
                                </button>
                              </div>
                              
                              {/* Voting Progress */}
                              <div className="mt-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Zustimmung
                                  </span>
                                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                                    {votingData.percentage}%
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                  <div 
                                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${Math.min(votingData.percentage, 100)}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Attendees Tab */}
                {activeTab === 'attendees' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Teilnehmer ({attendees.length})
                      </h3>
                      <div className="flex items-center space-x-2 text-sm">
                        {quorumStatus && (
                          <span className={`px-3 py-1 rounded-full font-medium border ${
                            quorumStatus.met 
                              ? 'bg-green-100 text-green-800 border-green-200' 
                              : 'bg-red-100 text-red-800 border-red-200'
                          }`}>
                            Quorum: {quorumStatus.present}/{quorumStatus.total}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {attendees.map((attendee) => (
                        <div key={attendee.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-white">
                                  {attendee.hoa_members?.full_name || 'Unbekannt'}
                                </h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {attendee.hoa_units?.label && `${attendee.hoa_units.label} • `}
                                  {((attendee.ownership_share_ppm || 0) / 10000).toFixed(2)}% Anteil
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`w-3 h-3 rounded-full ${
                                attendee.is_present ? 'bg-green-500' : 'bg-gray-300'
                              }`}></span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {attendee.is_present ? 'Anwesend' : 'Abwesend'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video Tab */}
                {activeTab === 'video' && (
                  <div className="space-y-4">
                    <div className="text-center py-8">
                      <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Video-Konferenz Integration
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 mb-6">
                        Jitsi Meet Integration für WEG-Versammlungen
                      </p>
                      
                      <div className="flex items-center justify-center space-x-4">
                        <button
                          onClick={() => setVideoEnabled(!videoEnabled)}
                          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                            videoEnabled 
                              ? 'bg-green-600 hover:bg-green-700 text-white' 
                              : 'bg-gray-600 hover:bg-gray-700 text-white'
                          }`}
                        >
                          {videoEnabled ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                          <span>{videoEnabled ? 'Video an' : 'Video aus'}</span>
                        </button>
                        
                        <button
                          onClick={() => setAudioEnabled(!audioEnabled)}
                          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                            audioEnabled 
                              ? 'bg-green-600 hover:bg-green-700 text-white' 
                              : 'bg-gray-600 hover:bg-gray-700 text-white'
                          }`}
                        >
                          {audioEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                          <span>{audioEnabled ? 'Mikro an' : 'Mikro aus'}</span>
                        </button>
                        
                        <button
                          onClick={joinVideoCall}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                        >
                          <Video className="w-4 h-4" />
                          <span>Konferenz beitreten</span>
                        </button>
                      </div>
                      
                      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          💡 <strong>Tipp:</strong> Die Video-Integration nutzt Jitsi Meet für sichere, 
                          browserbasierte Videokonferenzen ohne zusätzliche Software.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Live Voting Modal */}
      {showVotingModal && selectedMotion && (
        <LiveVotingModal
          motion={selectedMotion}
          isOpen={showVotingModal}
          onClose={() => {
            setShowVotingModal(false);
            setSelectedMotion(null);
          }}
          onVoteSubmitted={handleVoteSubmitted}
        />
      )}
    </>
  );
};

export default MeetingManagementModal;

